import React, { createContext, useContext, useEffect, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { listFiltersService, ListTableSort, getSortedCollection } from "sprint-common-web";

import { mapAirlinesToDisplay } from "../../../../../../common/utils/mapAirlinesToDisplay";
import { ContentVersionSource } from "../../../../../common/types/ContentVersionSource";
import { mapToVersionSource } from "../../../../../common/utils/mapToVersionSource";
import { EpisodeDetails, EpisodeVersionDetails, TvSeasonDetails, TvSeriesDetails } from "../../../../TvSeriesDetails";
import { EpisodePageContext } from "../../context/EpisodePageProvider";
import { UpdateEpisodeVersionParams } from "../edit-version-form/EditEpisodeVersionForm";
import { defaultEpisodeVersionListingFilters, EpisodeVersionListingFilters } from "../EpisodeVersionListingFilters";
import { EpisodeVersionListingItem } from "../EpisodeVersionListingItem";

type EpisodePageProviderProps = React.PropsWithChildren<unknown>;

export const EpisodeVersionListProvider: React.FunctionComponent<EpisodePageProviderProps> = ({
    children
}: EpisodePageProviderProps) => {
    const {
        addVersion,
        updateVersion,
        selectedEpisode,
        selectedTvSeries,
        selectedSeason,
        setVersionAsMerged
    } = useContext(EpisodePageContext);

    const [versionsToMerge, setVersionsToMerge] = useState<EpisodeVersionListingItem[]>([]);
    const [listingItems, setListingItems] = useState<EpisodeVersionListingItem[]>([]);
    const [totalItems, setTotalItems] = useState<number>(0);
    const filtersService = listFiltersService<EpisodeVersionListingFilters>(defaultEpisodeVersionListingFilters);

    const location = useLocation();
    const history = useHistory();
    const [filters, setFilters] = useState<EpisodeVersionListingFilters>(() =>
        filtersService.getSavedFilters(location.search)
    );

    useEffect(() => {
        if (filters) {
            filterListingItems(filters);
        }
    }, [filters]);

    const addEpisodeVersion = (newEpisodeVersion: EpisodeVersionDetails) => {
        setListingItems((prevItems: EpisodeVersionListingItem[]) => {
            if (prevItems.length >= filters.pageSize) prevItems.pop();
            const newListingItems = [
                ...mapToVersionItem([{ ...newEpisodeVersion, isModified: true }], false),
                ...prevItems
            ];
            setTotalItems(newListingItems.length);

            return newListingItems;
        });

        addVersion(newEpisodeVersion);
    };

    const updateEpisodeVersion = (updatedVersionData: UpdateEpisodeVersionParams) => {
        const versionListingItem = listingItems.find(
            (item: EpisodeVersionListingItem) => item.id === updatedVersionData.versionId
        );
        if (versionListingItem) {
            versionListingItem.distributorName = updatedVersionData.distributor.name;
            versionListingItem.typeName = updatedVersionData.type.name;
            versionListingItem.isTrailer = updatedVersionData.isTrailer;
            versionListingItem.code = updatedVersionData.code;
            versionListingItem.source = mapToVersionSource(versionListingItem);
        }
        setListingItems(listingItems);

        updateVersion(updatedVersionData);
    };

    const setSort = (sort: ListTableSort) => {
        saveAndSetFilters({
            sortBy: sort.by as keyof EpisodeVersionListingItem,
            sortOrder: sort.order,
            page: 1
        });
    };

    const saveAndSetFilters = (newFilters: Partial<EpisodeVersionListingFilters>): void => {
        setFilters((prevFilters: EpisodeVersionListingFilters) => {
            const updatedFilters = { ...prevFilters, ...newFilters };

            filtersService.saveFilters(location.pathname, history, updatedFilters);

            return updatedFilters;
        });
    };

    const clearFilters = (): void => {
        saveAndSetFilters(defaultEpisodeVersionListingFilters);
    };

    const filterListingItems = (filter: EpisodeVersionListingFilters) => {
        const filteredItems = getFilteredVersionItems(mapToVersionItem(selectedEpisode.versions), filter);
        setTotalItems(filteredItems.length);

        const sortedItems = getSortedCollection(filteredItems, filter.sortBy, filter.sortOrder);
        const currentPageItems = getCurrentPageItems(sortedItems, filter);
        setListingItems(currentPageItems);
    };

    const getCurrentPageItems = (
        items: EpisodeVersionListingItem[],
        filter: EpisodeVersionListingFilters
    ): EpisodeVersionListingItem[] => {
        const firstItem = (filter.page - 1) * filter.pageSize;
        const lastItem = filter.page * filter.pageSize;

        return items.slice(firstItem, lastItem);
    };

    const getFilteredVersionItems = (
        items: EpisodeVersionListingItem[],
        filter: EpisodeVersionListingFilters
    ): EpisodeVersionListingItem[] => {
        let filteredVersions = items;
        filteredVersions = filter.includeMerged
            ? filteredVersions
            : filteredVersions.filter(
                  (version: EpisodeVersionListingItem) => !version.merged === !filter.includeMerged
              );

        filteredVersions = filter.sources
            ? filteredVersions.filter((version: EpisodeVersionListingItem) => filter.sources?.includes(version.source))
            : filteredVersions;

        return filteredVersions;
    };

    const addVersionToMergeList = (versionItem?: EpisodeVersionListingItem) => {
        if (!versionItem) return;

        setVersionsToMerge((prevVersions: EpisodeVersionListingItem[]) => {
            prevVersions.push(versionItem);

            return [...prevVersions];
        });
    };

    const removeVersionFromMergeList = (versionItem: EpisodeVersionListingItem) => {
        setVersionsToMerge((prevVersions: EpisodeVersionListingItem[]) => {
            const filteredVersionsItems = prevVersions.filter(
                (item: EpisodeVersionListingItem) => item.id !== versionItem.id
            );

            return [...filteredVersionsItems];
        });
    };

    const setEpisodeVersionAsMerged = (versionId: string, mergedTargetId: string) => {
        setVersionAsMerged(versionId, mergedTargetId);
        filterListingItems(filters);
    };

    const clearVersionsToMerge = () => {
        setVersionsToMerge([]);
    };

    const setPage = (page: number) => {
        saveAndSetFilters({ page });
    };

    const setPageSize = (pageSize: number): void => {
        saveAndSetFilters({ pageSize, page: 1 });
    };

    const setSources = (sources?: ContentVersionSource[]): void => {
        saveAndSetFilters({ sources });
    };

    const setIncludeMerged = (includeMerged?: boolean): void => {
        saveAndSetFilters({ includeMerged });
    };

    return (
        <EpisodeVersionListContext.Provider
            value={{
                versionsToMerge,
                listingItems,
                totalItems,
                filters,
                selectedEpisode,
                selectedTvSeries,
                selectedSeason,
                clearFilters,
                setSort,
                setPage,
                setPageSize,
                setIncludeMerged,
                setSources,
                addEpisodeVersion,
                updateEpisodeVersion,
                addVersionToMergeList,
                removeVersionFromMergeList,
                clearVersionsToMerge,
                setEpisodeVersionAsMerged
            }}
        >
            {children}
        </EpisodeVersionListContext.Provider>
    );
};

const mapToVersionItem = (
    episodeVersions: EpisodeVersionDetails[],
    resetNewFlag = true
): EpisodeVersionListingItem[] => {
    return episodeVersions.map((version: EpisodeVersionDetails) => {
        return {
            id: version.id,
            distributorName: version.distributor.name,
            typeName: version.type.name,
            code: version.code,
            merged: !!version.mergedTargetId,
            orphaned: !version.buzzVersion,
            versionTypeId: version.type.id,
            isModified: resetNewFlag ? false : version.isModified,
            buzzVersion: version.buzzVersion,
            brandings: version.brandings ? mapAirlinesToDisplay(version.brandings) : "",
            isTrailer: version.isTrailer,
            source: mapToVersionSource(version)
        };
    });
};

interface IEpisodeVersionListContext {
    totalItems: number;
    filters: EpisodeVersionListingFilters;
    listingItems: EpisodeVersionListingItem[];
    versionsToMerge: EpisodeVersionListingItem[];
    selectedEpisode: EpisodeDetails;
    selectedTvSeries: TvSeriesDetails;
    selectedSeason: TvSeasonDetails;
    clearFilters(): void;
    addEpisodeVersion(versionDetails: EpisodeVersionDetails): void;
    updateEpisodeVersion(updatedVersionData: UpdateEpisodeVersionParams): void;
    setSort(sort: ListTableSort): void;
    setPage(page: number): void;
    setPageSize(pageSize: number): void;
    setSources(sources?: ContentVersionSource[]): void;
    setIncludeMerged(includeMerged?: boolean): void;
    clearVersionsToMerge(): void;
    addVersionToMergeList(versionItem?: EpisodeVersionListingItem): void;
    removeVersionFromMergeList(versionItem: EpisodeVersionListingItem): void;
    setEpisodeVersionAsMerged(versionId: string, mergedTargetId: string): void;
}

const defaultEpisodePageContext: IEpisodeVersionListContext = {} as IEpisodeVersionListContext;

export const EpisodeVersionListContext = createContext<IEpisodeVersionListContext>(defaultEpisodePageContext);
